import React, { useState } from 'react';
import { Box, Typography, Collapse, IconButton, useTheme } from '@mui/material';
import { IconChevronRight, IconPlayerPlayFilled, IconPlayerStopFilled } from '@tabler/icons-react';
import { splitHighlight } from '../../utils/searchUtils.js';

/**
 * Wrap any active search terms in `text` with the amber search-highlight (#1211,
 * color #1230). No terms -> the raw string, zero overhead. Matched runs render as
 * <mark> reading theme.palette.searchHighlight (dark = amber chip + dark ink;
 * light = translucent amber, text unchanged).
 */
const HighlightedText = ({ text, terms }) => {
  const theme = useTheme();
  if (!terms || !terms.length) return text;
  const segments = splitHighlight(text, terms);
  return (
    <>
      {segments.map((seg, i) =>
        seg.match ? (
          <Box
            key={i}
            component="mark"
            data-search-hit="true"
            sx={{
              bgcolor: theme.palette.searchHighlight.bg,
              color: theme.palette.searchHighlight.text,
              borderRadius: '2px',
            }}
          >
            {seg.text}
          </Box>
        ) : (
          <React.Fragment key={i}>{seg.text}</React.Fragment>
        ),
      )}
    </>
  );
};

/**
 * Renders one documentation section (#1064): a numbered, clickable heading that
 * collapses/expands its typed content blocks (#1083, inline accordion, collapsed
 * by default - controlled by DocsBody so search + TOC jumps can force-expand). A
 * "note" block is a nested progressive-disclosure row for the deeper detail.
 *
 * Presentational + theme-native: every size/color reads from theme.ds /
 * theme.palette (DESIGN_SYSTEM.md). No hardcoded hex.
 *
 * @param {object} props
 * @param {object} props.section    one entry from the KB package (content/knowledgeBase.js)
 * @param {boolean} [props.compact]  tighter spacing for the dock panel
 * @param {boolean} [props.expanded] whether the section body is open
 * @param {Function} [props.onToggle] toggle handler for the header
 */
const NoteBlock = ({ label, body, terms = [], forceOpen = false }) => {
  const theme = useTheme();
  const { ds, palette } = theme;
  const [open, setOpen] = useState(false);
  // During an active search, a note matching a term force-opens so the
  // highlighted hit inside it is visible (mirrors the section auto-expand).
  const isOpen = open || forceOpen;

  return (
    <Box
      sx={{
        mt: `${ds.space[3]}px`,
        borderLeft: `2px solid ${palette.divider}`,
        pl: `${ds.space[5]}px`,
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={isOpen}
        // Pin the clean label as the accessible name; inline highlight marks
        // would otherwise fragment it for screen readers (#1228).
        aria-label={label}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: `${ds.space[2]}px`,
          width: '100%',
          textAlign: 'left',
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          color: palette.interactive,
          fontSize: `${ds.font.meta}px`,
          fontWeight: ds.font.weightMedium,
          textTransform: 'uppercase',
          letterSpacing: ds.font.letterUpper,
          '&:focus-visible': {
            outline: `1.5px solid ${palette.interactive}`,
            outlineOffset: '2px',
          },
        }}
      >
        <IconChevronRight
          size={14}
          style={{
            transform: isOpen ? 'rotate(90deg)' : 'none',
            transition: `transform ${theme.ds.motion.fast} ${theme.ds.motion.easeOut}`,
            flexShrink: 0,
          }}
        />
        {/* One flex item: keep the highlighted label as a single inline run so
            the mark segments do not become separate flex children (#1228). */}
        <Box component="span" sx={{ minWidth: 0 }}>
          <HighlightedText text={label} terms={terms} />
        </Box>
      </Box>
      <Collapse in={isOpen} unmountOnExit>
        <Typography
          sx={{
            mt: `${ds.space[2]}px`,
            fontSize: `${ds.font.row}px`,
            lineHeight: 1.6,
            color: palette.text.dim,
          }}
        >
          <HighlightedText text={body} terms={terms} />
        </Typography>
      </Collapse>
    </Box>
  );
};

const DocsSection = ({
  section,
  compact = false,
  expanded = false,
  onToggle,
  searchTerms = [],
  audioAvailable = false,
  playing = false,
  onTogglePlay,
}) => {
  const theme = useTheme();
  const { ds, palette } = theme;

  const para = {
    fontSize: `${ds.font.row}px`,
    lineHeight: 1.6,
    color: palette.text.secondary,
    mt: `${ds.space[4]}px`,
  };

  return (
    <Box
      component="section"
      id={section.id}
      sx={{
        scrollMarginTop: compact ? `${ds.space[8]}px` : `${ds.space[14] + ds.space[10]}px`,
        pt: compact ? `${ds.space[5]}px` : `${ds.space[7]}px`,
        borderTop: `1px solid ${palette.divider}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: `${ds.space[3]}px` }}>
        <Box
          component="button"
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: `${ds.space[4]}px`,
            flex: 1,
            minWidth: 0,
            textAlign: 'left',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            '&:focus-visible': {
              outline: `1.5px solid ${palette.interactive}`,
              outlineOffset: '2px',
            },
          }}
        >
        <Typography
          component="span"
          sx={{
            fontSize: `${ds.font.meta}px`,
            fontWeight: ds.font.weightMedium,
            color: palette.text.faint,
            fontVariantNumeric: 'tabular-nums',
            flexShrink: 0,
          }}
        >
          {section.number}
        </Typography>
        <Typography
          component="h2"
          // Pin the clean title as the accessible name; inline highlight marks
          // would otherwise fragment the heading name for screen readers (#1228).
          aria-label={section.title}
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: `${compact ? ds.font.sectionTitle : ds.font.h1}px`,
            fontWeight: ds.font.weightMedium,
            color: palette.text.primary,
          }}
        >
          <HighlightedText text={section.title} terms={searchTerms} />
        </Typography>
        <IconChevronRight
          size={18}
          style={{
            color: palette.text.faint,
            transform: expanded ? 'rotate(90deg)' : 'none',
            transition: `transform ${ds.motion.fast} ${ds.motion.easeOut}`,
            flexShrink: 0,
          }}
        />
        </Box>
        {audioAvailable && (
          <IconButton
            size="small"
            onClick={onTogglePlay}
            aria-label={playing ? 'Stop narration' : 'Play narration'}
            sx={{ flexShrink: 0, color: playing ? palette.error.main : palette.interactive }}
          >
            {playing ? <IconPlayerStopFilled size={16} /> : <IconPlayerPlayFilled size={16} />}
          </IconButton>
        )}
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ pb: `${ds.space[4]}px` }}>
          {section.blocks.map((block, i) => {
            if (block.type === 'p') {
              return (
                <Typography key={i} sx={para}>
                  <HighlightedText text={block.text} terms={searchTerms} />
                </Typography>
              );
            }
            if (block.type === 'list' || block.type === 'steps') {
              const ordered = block.type === 'steps';
              return (
                <Box
                  key={i}
                  component={ordered ? 'ol' : 'ul'}
                  sx={{
                    mt: `${ds.space[4]}px`,
                    mb: 0,
                    pl: `${ds.space[8]}px`,
                    '& li': {
                      fontSize: `${ds.font.row}px`,
                      lineHeight: 1.6,
                      color: palette.text.secondary,
                      mb: `${ds.space[2]}px`,
                    },
                    '& li::marker': {
                      color: palette.text.faint,
                      fontVariantNumeric: 'tabular-nums',
                    },
                  }}
                >
                  {block.items.map((item, j) => (
                    <li key={j}>
                      <HighlightedText text={item} terms={searchTerms} />
                    </li>
                  ))}
                </Box>
              );
            }
            if (block.type === 'note') {
              const noteText = `${block.label} ${block.body}`.toLowerCase();
              const forceOpen = searchTerms.some((t) => noteText.includes(t));
              return (
                <NoteBlock
                  key={i}
                  label={block.label}
                  body={block.body}
                  terms={searchTerms}
                  forceOpen={forceOpen}
                />
              );
            }
            return null;
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

export default DocsSection;
